import { useState } from 'react';
import { calcularProgressao, getStatusColor } from '@/utils/progressaoUtils';
import type { ResumoFuncionario } from './FuncionarioResumoCard';

interface PontosInfo {
  total: number;
  limiarEfetivo?: number;
  limiar?: number;
  regraEspecial18Anos?: boolean;
}

interface ResumoCarreiraGroupProps {
  carreira: string;
  resumos: ResumoFuncionario[];
  pontosPorFuncionario: Record<string, PontosInfo>;
  defaultLimiar: number;
  onCardClick: (r: ResumoFuncionario) => void;
}

const avaliacaoConfig: Record<string, { dot: string }> = {
  Excelente:   { dot: 'bg-emerald-500' },
  'Muito Bom': { dot: 'bg-teal-500'    },
  Bom:         { dot: 'bg-amber-500'   },
  Regular:     { dot: 'bg-orange-500'  },
  Inadequado:  { dot: 'bg-red-500'     },
  Relevante:   { dot: 'bg-teal-400'    },
  Adequado:    { dot: 'bg-zinc-400'    },
};

const avatarPalette = [
  { bg: 'bg-rose-100',   text: 'text-rose-600'   },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100',  text: 'text-amber-600'  },
  { bg: 'bg-teal-100',   text: 'text-teal-600'   },
  { bg: 'bg-cyan-100',   text: 'text-cyan-600'   },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

function fmt(n: number) {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

export default function ResumoCarreiraGroup({
  carreira,
  resumos,
  pontosPorFuncionario,
  defaultLimiar,
  onCardClick,
}: ResumoCarreiraGroupProps) {
  const [collapsed, setCollapsed] = useState(false);

  const elegiveis = resumos.filter((r) => {
    const info = pontosPorFuncionario[r.id];
    const pontos = info?.total ?? 0;
    const limiar = info?.limiarEfetivo ?? info?.limiar ?? defaultLimiar;
    return calcularProgressao(pontos, r.nivelAtual, limiar).status === 'pronto';
  });

  const mediaPontos = resumos.length > 0
    ? resumos.reduce((s, r) => s + (pontosPorFuncionario[r.id]?.total ?? 0), 0) / resumos.length
    : 0;

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
      {/* Header do grupo */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-zinc-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-briefcase-line text-zinc-600 text-sm" />
          </div>
          <div className="text-left">
            <p className="text-[14px] font-bold text-zinc-800">{carreira}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[11px] text-zinc-400">{resumos.length} funcionário{resumos.length !== 1 ? 's' : ''}</span>
              <span className="text-zinc-200">·</span>
              <span className="text-[11px] text-zinc-400">Média: <span className="font-semibold text-zinc-600">{fmt(mediaPontos)} pts</span></span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {elegiveis.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
              {elegiveis.length} elegíve{elegiveis.length === 1 ? 'l' : 'is'}
            </span>
          )}
          {/* Mini barra */}
          <div className="w-24 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all"
              style={{ width: resumos.length > 0 ? `${(elegiveis.length / resumos.length) * 100}%` : '0%' }}
            />
          </div>
          <div className="w-5 h-5 flex items-center justify-center text-zinc-400">
            <i className={`${collapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-base`} />
          </div>
        </div>
      </button>

      {/* Tabela de funcionários */}
      {!collapsed && (
        <div className="border-t border-zinc-100 overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-zinc-50/60">
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Funcionário</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Nível</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Avaliações</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Pontos Ciclo</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Progresso</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Última Aval.</th>
                <th className="px-5 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {resumos.map((r, idx) => {
                const info = pontosPorFuncionario[r.id];
                const pontos = info?.total ?? 0;
                const limiar = info?.limiarEfetivo ?? info?.limiar ?? defaultLimiar;
                const prog = calcularProgressao(pontos, r.nivelAtual, limiar);
                const colors = getStatusColor(prog.status);
                const isElegivel = prog.status === 'pronto';
                const palette = avatarPalette[idx % avatarPalette.length];
                const initials = getInitials(r.nome);
                const sorted = [...r.registos].sort((a, b) => a.ano - b.ano);
                const lastReg = sorted[sorted.length - 1];

                return (
                  <tr
                    key={r.id}
                    onClick={() => onCardClick(r)}
                    className="border-t border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer"
                  >
                    {/* Nome */}
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[12px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                          {initials}
                        </div>
                        <div>
                          <p className="text-[12px] font-semibold text-zinc-800 whitespace-nowrap">{r.nome}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            {info?.regraEspecial18Anos && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                <i className="ri-award-line text-[8px]" />18 anos
                              </span>
                            )}
                            {isElegivel && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse inline-block" />
                                Elegível
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Nível */}
                    <td className="px-5 py-3">
                      <span className="text-[12px] font-semibold text-zinc-700 bg-zinc-100 px-2 py-0.5 rounded-full">
                        Nível {r.nivelAtual}
                      </span>
                    </td>
                    {/* Avaliações */}
                    <td className="px-5 py-3">
                      <span className="text-[12px] text-zinc-500">{r.registos.length}</span>
                    </td>
                    {/* Pontos */}
                    <td className="px-5 py-3">
                      <span className={`text-[13px] font-bold ${colors.text}`}>{fmt(pontos)} pts</span>
                      <span className="text-[10px] text-zinc-300 ml-1">/ {limiar}</span>
                    </td>
                    {/* Barra de progresso */}
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 min-w-[100px]">
                        <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${colors.bar}`}
                            style={{ width: `${prog.percentagem}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-zinc-400 flex-shrink-0">{prog.percentagem.toFixed(0)}%</span>
                      </div>
                    </td>
                    {/* Última avaliação */}
                    <td className="px-5 py-3">
                      {lastReg ? (
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${avaliacaoConfig[lastReg.avaliacao]?.dot ?? 'bg-zinc-300'}`} />
                          <span className="text-[11px] text-zinc-500 whitespace-nowrap">
                            {lastReg.avaliacao} · {lastReg.ano}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-zinc-300 italic">—</span>
                      )}
                    </td>
                    {/* Detalhe */}
                    <td className="px-5 py-3">
                      <div className="w-6 h-6 flex items-center justify-center text-zinc-300 group-hover:text-zinc-500">
                        <i className="ri-arrow-right-s-line text-base" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
